import { parseCanvaPublishDraftId } from './canvaPublishDraft';

/**
 * Whether this browser session was opened from Canva.
 *
 * A Canva buyer is an SSO buyer by account type, but must not get the SSO-buyer shop experience:
 * that experience is built around a label shape only the SSO-buyer endpoints return, and a Canva
 * session reads the ordinary shop endpoints instead. Every branch keyed on account type alone
 * sends them down the wrong path — see `useSsoBuyerExperience`.
 *
 * Remembered for the session rather than read off the URL each time. The parked design id is only
 * on the gallery link; the moment the buyer opens a product it is gone, and a rule that depends on
 * it would silently flip back halfway through the flow. Held in `sessionStorage` so it dies with
 * the tab and cannot leak into an ordinary login later in the same browser.
 */
const CANVA_SESSION_KEY = 'canvaSession';

/**
 * The parked design itself, remembered for the same reason the session is: the id travels on the
 * gallery link only, and the shop has entry points into a product that drop the query string
 * entirely (the header's Shop menu, for one). Without this, following one of those loses the
 * design and the buyer silently falls back into the ordinary "configure a job from scratch" flow.
 *
 * Cleared as soon as a publish succeeds -- the design has been used, and republishing it would
 * ask the server for a draft it has already consumed.
 */
const CANVA_DRAFT_KEY = 'canvaSessionDraftId';

/**
 * The products the hand-off restricted this visit to, as the gallery's own query
 * ({@code skuTags=...}). Remembered with the design for the same reason: it is only on the
 * gallery link, and "choose a different product" has to return the buyer to the same restricted
 * set rather than to the whole catalogue the tags were there to narrow.
 */
const CANVA_SKU_FILTER_KEY = 'canvaSessionSkuFilter';

/**
 * Called at both entry points: the SSO exchange, and landing on a link carrying a parked design.
 *
 * @param draftId the parked design, when the caller knows it
 */
export const markCanvaSession = (
  draftId?: string,
  skuFilterSearch?: string
): void => {
  try {
    sessionStorage.setItem(CANVA_SESSION_KEY, '1');
    if (draftId) {
      sessionStorage.setItem(CANVA_DRAFT_KEY, draftId);
      // Written together with the design, including when it is empty: an untagged hand-off has
      // to clear a filter left over from an earlier design in the same tab.
      sessionStorage.setItem(CANVA_SKU_FILTER_KEY, skuFilterSearch || '');
    }
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). The URL check below still
    // covers the gallery, which is where the difference matters most.
  }
};

/** The parked design this session is shopping for, if it still has one. */
export const getCanvaSessionDraftId = (): string | undefined => {
  try {
    return sessionStorage.getItem(CANVA_DRAFT_KEY) || undefined;
  } catch {
    return undefined;
  }
};

/** The products that design may go on, as a gallery query, when the hand-off named them. */
export const getCanvaSessionSkuFilter = (): string | undefined => {
  try {
    return sessionStorage.getItem(CANVA_SKU_FILTER_KEY) || undefined;
  } catch {
    return undefined;
  }
};

/** Called once a publish has consumed the design, so nothing tries to publish it twice. */
export const clearCanvaSessionDraftId = (): void => {
  try {
    sessionStorage.removeItem(CANVA_DRAFT_KEY);
    sessionStorage.removeItem(CANVA_SKU_FILTER_KEY);
  } catch {
    // nothing to clear if storage is unavailable
  }
};

export const clearCanvaSession = (): void => {
  try {
    sessionStorage.removeItem(CANVA_SESSION_KEY);
    sessionStorage.removeItem(CANVA_DRAFT_KEY);
    sessionStorage.removeItem(CANVA_SKU_FILTER_KEY);
  } catch {
    // nothing to clear if storage is unavailable
  }
};

export const isCanvaSession = (): boolean => {
  try {
    if (sessionStorage.getItem(CANVA_SESSION_KEY) === '1') {
      return true;
    }
  } catch {
    // fall through to the URL, which is still authoritative on the landing page
  }
  return Boolean(parseCanvaPublishDraftId(window.location.search));
};
